import { elementTypes, NOT_VERY_EFFECTIVE, SUPER_EFFECTIVE, type Element } from '../../shared/elements.ts';
import { BONUS_MAX } from '../../shared/stats.ts';
import { ENERGY_MAX, ENERGY_BENCH_PERCENT, ENERGY_REST_PERCENT, MAX_MOVES, STATUS_KEYS, STATUSES } from '../../shared/moves.ts';
import { DEFEAT_COIN_PENALTY, DUEL_TURN_TIMEOUT_MS, DUEL_WIN_COINS, SHINY_CHANCE, STARTER_LEVEL, TEAM_SIZE } from '../../shared/game-rules.ts';
import { describeItemEffect, STARTING_BAG, STARTING_COINS } from '../../shared/items.ts';
import { itemCatalog, itemVisual } from '../common/item-catalog.ts';
import { RARITIES, RARITY_KEYS } from '../../shared/rarity.ts';
import { $, el } from '../common/dom.ts';
import { richText } from '../common/icons.ts';
import { elementBadge } from '../common/element-badge.ts';
import { Modal } from '../common/modal.ts';
import { rarityBadge } from '../common/rarity-badge.ts';
import { creatureText, gameConfig } from '../../shared/game-config.ts';

const SEEN_KEY = 'pocket_help_seen';

type Content = Node | string;

interface Section {
  id: string;
  title: string;
  body: () => Content[];
}

/**
 * Les textes de la notice peuvent contenir des icônes : `[[nom]]` (voir public/assets/icons), et le nom des créatures
 * (`{monstre}`, `{un monstre}`… : voir creatureText).
 */
const rich = (children: Content[]) => children.flatMap((child) => (typeof child === 'string' ? richText(creatureText(child)) : [child]));
const p = (...children: Content[]) => el('p', {}, ...rich(children));
const b = (text: string) => el('b', {}, ...richText(creatureText(text)));
const kbd = (text: string) => el('kbd', { textContent: text });
const list = (...items: Content[][]) => el('ul', {}, ...items.map((item) => el('li', {}, ...rich(item))));
const table = (head: string[], rows: Content[][]) => el('table', {},
  el('thead', {}, el('tr', {}, ...head.map((cell) => el('th', { textContent: cell })))),
  el('tbody', {}, ...rows.map((row) => el('tr', {}, ...row.map((cell) => el('td', {}, ...rich([cell])))))));

const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;
const badges = (elements: readonly Element[]) =>
  elements.length ? el('span', { className: 'badges' }, ...elements.map(elementBadge)) : el('span', { className: 'muted', textContent: '—' });
const startingBag = () => Object.entries(STARTING_BAG).flatMap(([id, n]) => {
  const item = itemCatalog.get(id);
  return item ? [`${n} ${item.name}${n > 1 ? 's' : ''}`] : [];
}).join(' et ');

const SECTIONS: Section[] = [
  {
    id: 'but',
    title: '[[target]] Le but',
    body: () => [
      p('Explorez le monde, rencontrez des {monstres} dans les hautes herbes, ', b('capturez-les'), ' pour compléter votre {Dex}, ',
        'faites-les ', b('évoluer'), ' et affrontez les autres dresseurs en ', b('duel'), '.'),
      p(`Vous commencez avec {le monstre} de votre choix au niveau ${STARTER_LEVEL}, ${STARTING_COINS} pièces, ${startingBag()}.`),
    ],
  },
  {
    id: 'commandes',
    title: '[[gamepad]] Commandes',
    body: () => [table(['Touche', 'Action'], [
      [el('span', {}, kbd('↑ ↓ ← →'), ' ou ', kbd('ZQSD'), ' / ', kbd('WASD')), 'Se déplacer'],
      [el('span', {}, kbd('1'), ' ', kbd('2'), ' ', kbd('3'), ' ', kbd('4')), 'Attaques en combat'],
      [kbd('R'), 'Repos en combat (récupère de l\'énergie)'],
      [kbd('E'), 'Équipe'],
      [kbd('C'), 'Carte du dresseur'],
      [kbd('P'), '{Dex}'],
      [kbd('J'), 'Journal de quêtes'],
      [kbd('M'), 'Carte du monde : où vous êtes'],
      [kbd('U'), 'Succès'],
      [kbd('Espace'), 'Parler / utiliser ce qui est en face (PNJ, borne, comptoir) · avancer un dialogue · accélérer un combat'],
      [kbd('H'), 'Cette notice'],
      [kbd('Entrée'), 'Discuter avec les joueurs de la carte'],
      [kbd('/unstuck'), 'Dans le chat : bloqué ? retour au point de départ de la carte'],
      [kbd('Maj'), 'Courir (maintenir)'],
      [kbd('Échap'), 'Fermer une fenêtre ou le menu'],
      ['Toucher / cliquer un dresseur', 'Inspecter son équipe, ou le défier en duel'],
    ]), p('Sur téléphone : la croix en bas à gauche pour marcher ; en bas à droite, A pour parler ou utiliser ce qui est en face (il s\'allume quand c\'est possible) et B à maintenir pour courir. Le bouton Menu, en haut à droite, ouvre l\'équipe, {le dex}, la carte, les quêtes, le chat… En combat, touchez l\'écran pour accélérer les animations.')],
  },
  {
    id: 'monde',
    title: '[[map]] Le monde',
    body: () => [list(
      [b('[[zone]] Zones de farm'), ' (souvent les hautes herbes) : chaque pas peut déclencher une rencontre. Le badge en haut à gauche indique combien d\'espèces de la zone vous avez déjà capturées, par exemple ', b('[[capture]] 2/4'), '. Les icônes [[moon]] [[rain]] [[sun]]… signalent que certaines espèces n\'y apparaissent que la nuit, sous la pluie, etc.'],
      [b('[[home]] Maisons'), ' : certaines portes mènent à l\'intérieur. Celle du village abrite le centre de soin et la boutique.'],
      [b('[[heal]] Borne de soin'), ' : placez-vous face à elle et appuyez sur ', kbd('Espace'), ' (bouton ', b('A'), ' sur mobile) pour soigner toute votre équipe. Elle devient aussi votre point de réapparition.'],
      [b('[[shop]] Comptoir'), ' : même geste face au comptoir pour acheter capsules et potions avec vos pièces.'],
      [b('Passages'), ' : certains bords de carte mènent vers d\'autres lieux… et d\'autres {monstres}.'],
    )],
  },
  {
    id: 'pnj',
    title: '[[npc]] Habitants, quêtes et dresseurs',
    body: () => [list(
      ['Placez-vous face à un habitant et appuyez sur ', kbd('Espace'), ' (', b('A'), ' sur mobile) pour lui parler. Encore ', kbd('Espace'), ' pour faire défiler le dialogue.'],
      ['Au-dessus d\'un habitant : ', b('!'), ' il a une quête pour vous, ', b('?'), ' vous pouvez lui rendre une quête terminée, ', b('[[mail]]'), ' une de vos quêtes vous demande d\'aller le voir, ', b('[[sword]]'), ' c\'est un dresseur que vous n\'avez pas encore battu.'],
      [b('Quêtes'), ' : capturer des {monstres} (d\'une espèce ou d\'un type), gagner des combats ou des duels, battre un dresseur, porter un message, livrer des objets ou montrer {un monstre} à un habitant, explorer un lieu, faire évoluer ou monter de niveau ses {monstres}, faire des achats, gagner un badge… Suivez-les dans le [[quests]] journal (', kbd('J'), ') et revenez voir le donneur pour votre récompense.'],
      [b('Dresseurs'), ' : s\'ils vous voient passer devant eux, ils vous défient ! Pas de capture ni de fuite contre un dresseur ; ses {monstres} arrivent les un{·e}s après les autres. Une victoire rapporte des pièces, une défaite vous renvoie au centre de soin.'],
    )],
  },
  {
    id: 'badges',
    title: '[[badge]] Arènes et badges',
    body: () => [list(
      ['Chaque arène a ses dresseurs et, tout au fond, un ', b('champion'), '. Battez-le pour recevoir son ', b('badge'), ' !'],
      ['Vos badges sont exposés sur votre [[user]] carte du dresseur (', kbd('C'), '), avec votre progression ; les autres joueurs peuvent l\'examiner.'],
      ['Certains passages sont ', b('[[lock]] verrouillés'), ' : il faut le bon badge pour les franchir.'],
      ['La première arène, l\'', b('Arène Verdure'), ', se trouve au sud du village : ses {monstres} sont de type Plante… pensez au Feu !'],
    )],
  },
  {
    id: 'combat',
    title: '[[sword]] Le combat',
    body: () => [
      p('Chaque {monstre} connaît jusqu\'à ', b(`${MAX_MOVES} attaques`), '. À chaque tour, choisissez-en une : le plus ', b('rapide'), ' agit en premier, sauf si une attaque a une ',
        b('priorité'), ' (Vive-Attaque…). Chaque attaque coûte de l\'', b('énergie [[energy]]'), ` : {un monstre} commence le combat avec ${ENERGY_MAX}, et elle ne revient pas toute seule. À la place d'une attaque, `, b('[[rest]] Repos'), ` (touche `, kbd('R'), `) lui en rend ${ENERGY_REST_PERCENT} % — mais l'adversaire, lui, peut attaquer. {Un monstre} mis en réserve (dans l'équipe, pas K.O.) en récupère ${ENERGY_BENCH_PERCENT} % à chaque tour : changer de {monstre} laisse souffler celui qui sort.`),
      p('Les dégâts dépendent de la ', b('puissance'), ' de l\'attaque, du niveau et de l\'attaque du lanceur, de la défense de la cible et des ', b('types'),
        ` : ×${1.5} si l'attaque est du même type que le lanceur, et super efficace ou non selon la cible. Une attaque peut rater (précision) ou être `, b('critique'), '.'),
      list(
        ['Effets : crans de statistique (Attaque, Défense, Vitesse, Précision, Esquive : ±6 au plus, remis à zéro en changeant de {monstre}), soin, drain, contrecoup, peur.'],
        ['Statuts : ', ...STATUS_KEYS.map((key, i) => el('span', {}, i ? ', ' : '', b(STATUSES[key].label))), ` — le poison et la brûlure blessent à chaque tour, la brûlure divise l'attaque, la paralysie ralentit et peut empêcher d'agir, le sommeil dure 1 à 3 tours, le gel se dissipe au hasard. Les statuts disparaissent à la fin du combat.`],
        [b('Capturer'), ', utiliser un ', b('objet'), ' ou ', b('changer'), ' de {monstre} se fait avant les attaques et coûte votre tour. {Un monstre} endormi{·e} ou gelé{·e} est plus facile à capturer.'],
        [b('Fuir'), ' réussit plus souvent si votre {monstre} est plus rapide.'],
        ['En montant de niveau, {un monstre} apprend de nouvelles attaques. Depuis sa ', b('fiche'), ', vous choisissez librement ses 4 attaques parmi toutes celles apprises jusqu\'à son niveau.'],
        ['Après une victoire, {le monstre} sur le terrain gagne toute l\'expérience', ...(gameConfig().teamXpShare > 0 ? [', et ', b(`chaque autre {monstre} de l'équipe (même K.O.) en reçoit ${gameConfig().teamXpShare} %`)] : []), '.'],
        ['Si votre {monstre} est K.O., envoyez-en {un} autre. Si toute l\'équipe est K.O. (contre un sauvage ou un dresseur), vous perdez ', b(`${DEFEAT_COIN_PENALTY * 100} % de vos pièces`), ' et retournez au dernier centre de soin, soignés. Un duel perdu ne coûte rien.'],
        [b('Combat double'), ' (certains dresseurs, et tous les combats en coop) : deux {monstres} de chaque côté. Chacun reçoit son ordre à son tour, puis, pour une attaque, choisissez sa ', b('cible'), ' parmi les adversaires. Le bouton [[undo]] revient sur l\'ordre déjà donné.'],
      ),
    ],
  },
  {
    id: 'types',
    title: '[[type-fire]] Les types',
    body: () => [
      p(`Une attaque super efficace inflige ×${SUPER_EFFECTIVE} dégâts, une attaque peu efficace ×${NOT_VERY_EFFECTIVE}. Le type qui compte est celui de l'attaque utilisée ; celui de la cible décide de l'efficacité. En combat, chaque attaque affiche son efficacité contre {le monstre} d'en face ([[arrow-up]] super efficace, [[arrow-down]] peu efficace, [[star]] même type que votre {monstre}) ; survolez le type adverse pour voir ses faiblesses.`),
      p(`Certain{·e}s {monstres} ont deux types : l'efficacité d'une attaque se multiplie contre chacun. Deux faiblesses se cumulent (×${SUPER_EFFECTIVE} × ×${SUPER_EFFECTIVE} = ×${Math.round(SUPER_EFFECTIVE * SUPER_EFFECTIVE * 100) / 100}), une faiblesse et une résistance se compensent en partie. Le bonus de même type vaut pour l'un ou l'autre de ses types.`),
      table(['Type', 'Super efficace contre', 'Peu efficace contre', 'Insensible à'], elementTypes.all().map((type) => [
        elementBadge(type.key), badges(type.strongAgainst), badges(type.weakAgainst),
        type.statusImmunities.map((status) => STATUSES[status].label).join(', ') || '—',
      ])),
    ],
  },
  {
    id: 'capture',
    title: '[[capture]] Capture et objets',
    body: () => [
      p('La chance de capture dépend de la ', b('rareté'), ' {du monstre}, de ses ', b('PV restants'), ' (affaiblissez-{le} d\'abord !) et de la ', b('capsule'), ' utilisée.'),
      table(['Objet', 'Effet', 'Prix'], [
        ...[...itemCatalog.captures(), ...itemCatalog.heals()].map((item) => [el('span', {}, itemVisual(item), ` ${item.name}`), describeItemEffect(item.effect), `${item.price} [[coins]]`]),
      ]),
      p('Les objets en vente changent d\'une boutique à l\'autre. Les soins s\'utilisent en combat ([[bag]] Sac) ou depuis l\'équipe (bouton Soigner, ou Donner un objet dans la fiche {du monstre}). {Un monstre} K.O. ne se soigne qu\'au centre de soin.'),
    ],
  },
  {
    id: 'raretes',
    title: '[[star]] Raretés et chromatiques',
    body: () => [
      table(['Rareté', 'Fréquence relative', 'Capture de base', 'Récompenses'], RARITY_KEYS.map((key) => [
        rarityBadge(key), String(RARITIES[key].spawnWeight), percent(RARITIES[key].captureRate), `×${RARITIES[key].xpMultiplier}`,
      ])),
      p(`[[sparkles]] Environ {un monstre} sauvage sur ${Math.round(1 / SHINY_CHANCE)} est `, b('chromatique'), ' : couleurs inhabituelles et scintillement. {Il} garde ce look une fois capturé{·e} ! Certaines zones les rendent plus fréquent{·e}s : le bandeau de la zone affiche alors [[sparkles]] ×2, ×5…'),
    ],
  },
  {
    id: 'progression',
    title: '[[stats]] Progression',
    body: () => [list(
      ['Chaque victoire rapporte de l\'', b('expérience'), ' {au monstre} qui a porté le dernier coup, et des ', b('pièces'), ' au dresseur. Les {monstres} rares rapportent davantage.'],
      ['En montant de niveau, {un monstre} gagne des stats et des PV.'],
      [b('Évolution'), ' : certaines espèces évoluent en atteignant un niveau précis. {Le dex} indique le niveau, et le nom de l\'évolution une fois découverte.'],
    )],
  },
  {
    id: 'equipe',
    title: '[[bag]] Équipe et {Dex}',
    body: () => [list(
      [`Jusqu'à ${TEAM_SIZE} {monstres} dans l'équipe ; les autres vont dans la `, b('boîte'), '. {Le monstre} ', b('en tête'), ' est envoyé{·e} en premier au combat : glissez les cartes (ou ▲ ▼) pour changer l\'ordre.'],
      ['[[storage]] La boîte se consulte devant un ', b('PC de stockage'), ' : échangez les {monstres} entre équipe et boîte, ou relâchez-en.'],
      ['[[rename]] Donnez un ', b('surnom'), ' à vos {monstres}.'],
      ['[[summary]] Touchez {un monstre} pour ouvrir sa ', b('fiche'), ` : ses statistiques détaillées, ses `, b('bonus'), ` personnels (0 à ${BONUS_MAX} par statistique, tirés à sa rencontre : deux {monstres} d'une même espèce ne se valent pas) et les objets à lui donner.`],
      [b('{Dex}'), ' : une espèce inconnue apparaît en silhouette ; ', b('vue'), ', elle révèle nom, type et habitats ; ', b('capturée'), ', sa description aussi. Relâcher {un monstre} n\'efface pas sa fiche.'],
    )],
  },
  {
    id: 'duels',
    title: '[[duel]] Duels',
    body: () => [list(
      ['Touchez (ou cliquez) un autre dresseur de la même carte pour le ', b('défier'), '. Il doit accepter l\'invitation.'],
      ['Les deux joueurs choisissent leur coup ', b('en secret'), ` : le tour se résout quand les deux ont joué (${DUEL_TURN_TIMEOUT_MS / 1000} s maximum, sinon un coup est tiré au hasard).`],
      ['Changer de {monstre} passe avant l\'attaque adverse… mais {le monstre} qui entre encaisse le coup.'],
      ['Duel ', b('amical'), ` : vos {monstres} combattent en pleine forme et ne sont pas blessé{·e}s. Le vainqueur gagne ${DUEL_WIN_COINS} pièces, de l'expérience, et une victoire de plus sur sa [[user]] carte du dresseur.`],
      ['Abandonner (ou se déconnecter) donne la victoire à l\'adversaire.'],
    )],
  },
  {
    id: 'coop',
    title: '[[users]] Coop',
    body: () => [list(
      ['Placez-vous juste à côté d\'un autre dresseur, touchez-le et choisissez ', b('Faire équipe'), '. Il doit accepter. La pastille en haut de l\'écran permet de quitter la coop (hors combat) ; une déconnexion y met fin.'],
      [b('Combats à deux'), ' : quand l\'un de vous déclenche un combat, l\'autre le rejoint. Vos trois premiers {monstres} forment une équipe de six : chacun commande un {monstre} sur le terrain et peut y envoyer n\'importe lequel des six. Deux {monstres} sauvages surgissent à la fois, et les dresseurs envoient deux {monstres}.'],
      ['L\'expérience va aux {monstres} sur le terrain à la fin (une part au reste de l\'équipe), les ', b('pièces sont partagées'), ' ; un objet laissé par {un monstre} ne va qu\'à l\'un de vous. {Un monstre} capturé revient à celui qui a lancé la capsule. Une défaite vous ramène tous les deux au centre de soin.'],
      [b('Ensemble partout'), ' : un changement de carte emmène les deux joueurs, et ce qui bloque l\'un (PNJ, buisson, badge, rocher) bloque l\'autre. Une borne de soin soigne les deux équipes.'],
      [b('Quêtes'), ' : celui qui parle au PNJ accepte la quête, l\'autre voit le dialogue ; elle démarre pour vous deux et sa progression est partagée. Une quête déjà faite par l\'un peut être refaite avec l\'autre : elle ne compte que pour lui (celui qui l\'a déjà terminée ne la reprend pas, mais l\'aide à la remplir et peut la rendre pour lui).'],
    )],
  },
  {
    id: 'astuces',
    title: '[[info]] Astuces',
    body: () => [list(
      ['Affaiblissez {un monstre} rare avant de lancer une capsule, et gardez vos Hyper Capsules pour les légendaires.'],
      ['Emmenez des {monstres} de types variés : un bon type change tout.'],
      ['Soignez-vous avant d\'explorer une nouvelle zone : les {monstres} y sont souvent de plus haut niveau.'],
    )],
  },
];

/** Notice du jeu : règles et possibilités, générées à partir des données partagées. */
export class HelpPanel extends Modal {
  private readonly nav = $('.help-nav', this.root);
  private readonly content = $('.help-content', this.root);

  constructor() {
    super('#help');
    $('#btn-help').addEventListener('click', () => this.toggle());
  }

  /**
   * La notice est construite à chaque ouverture : ses tableaux (types, objets…) viennent des catalogues reçus du
   * serveur, absents au lancement du jeu et modifiables depuis l'administration.
   */
  override open(): void {
    this.build();
    super.open();
  }

  /** Ouvre la notice à la première partie sur ce navigateur. */
  openOnFirstVisit(): void {
    if (localStorage.getItem(SEEN_KEY)) return;
    localStorage.setItem(SEEN_KEY, '1');
    this.open();
  }

  private build(): void {
    this.nav.replaceChildren(...SECTIONS.map((section) => {
      const link = el('button', {}, ...richText(creatureText(section.title)));
      link.addEventListener('click', () => $(`#help-${section.id}`, this.content).scrollIntoView({ behavior: 'smooth', block: 'start' }));
      return link;
    }));
    this.content.replaceChildren(...SECTIONS.map((section) =>
      el('section', { id: `help-${section.id}` }, el('h3', {}, ...richText(creatureText(section.title))), ...section.body())));
  }
}
